import React, { useState } from 'react';
import {
  QuestionMarkCircleIcon,
  ChatBubbleLeftRightIcon,
  PhoneIcon,
  EnvelopeIcon,
  ChevronDownIcon,
  ChevronUpIcon
} from '@heroicons/react/24/outline';

const RiderSupport: React.FC = () => {
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    subject: '',
    message: ''
  });

  const faqs = [
    {
      question: 'How do I accept a delivery job?',
      answer: 'Browse available jobs from the Available Jobs page. Click the "Accept" button on any job that matches your vehicle and schedule. The job will be added to your active jobs list.'
    },
    {
      question: 'When do I get paid for deliveries?',
      answer: 'Payments are processed immediately after successful delivery confirmation via OTP. Funds are added to your wallet and can be withdrawn within 1-3 business days.'
    },
    {
      question: 'How does the OTP delivery confirmation work?',
      answer: 'When you arrive at the dropoff location, ask the customer for their 6-digit OTP code. Enter this code in your app to complete the delivery and receive payment.'
    },
    {
      question: 'What is the commission rate?',
      answer: 'Volexx charges a 15% commission on each delivery. The remaining 85% is your net earning and is deposited directly to your wallet.'
    },
    {
      question: 'How do I update my vehicle information?',
      answer: 'Go to Profile & Settings from the navigation menu. You can update your vehicle type, license plate, and other vehicle details there.'
    },
    {
      question: 'What should I do if I encounter an issue during delivery?',
      answer: 'Contact our support team immediately through this page. You can also call our 24/7 hotline at +1 (555) 123-4567 for urgent assistance.'
    }
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // TODO: Submit support ticket
    console.log('Support ticket submitted:', formData);
    setFormData({ subject: '', message: '' });
  };

  return (
    <div className="min-h-screen bg-white py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Support & Help
          </h1>
          <p className="mt-2 text-gray-600">Get help and find answers to your questions</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contact Options */}
          <div className="lg:col-span-1 space-y-4">
            <div className="bg-white rounded-volexx shadow-volexx border border-gray-100 p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Contact Us</h2>
              <div className="space-y-4">
                <a
                  href="tel:+15551234567"
                  className="flex items-center p-3 bg-volexx-blue-50 rounded-volexx hover:bg-volexx-blue-100 transition-colors"
                >
                  <div className="bg-volexx-blue-600 p-2 rounded-volexx">
                    <PhoneIcon className="h-5 w-5 text-white" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-900">Call Us</p>
                    <p className="text-xs text-gray-600">+1 (555) 123-4567</p>
                  </div>
                </a>
                <a
                  href="mailto:support@volexx.com"
                  className="flex items-center p-3 bg-volexx-orange-50 rounded-volexx hover:bg-volexx-orange-100 transition-colors"
                >
                  <div className="bg-volexx-orange-600 p-2 rounded-volexx">
                    <EnvelopeIcon className="h-5 w-5 text-white" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-900">Email Us</p>
                    <p className="text-xs text-gray-600">support@volexx.com</p>
                  </div>
                </a>
                <div className="flex items-center p-3 bg-green-50 rounded-volexx">
                  <div className="bg-green-600 p-2 rounded-volexx">
                    <ChatBubbleLeftRightIcon className="h-5 w-5 text-white" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-900">Live Chat</p>
                    <p className="text-xs text-gray-600">Coming soon</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-volexx-blue-50 border border-volexx-blue-100 rounded-volexx p-4">
              <p className="text-sm text-volexx-blue-800">
                <strong>24/7 Support Available</strong><br />
                Our support team is here to help you anytime, anywhere.
              </p>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* FAQs */}
            <div className="bg-white rounded-volexx shadow-volexx border border-gray-100">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-xl font-bold text-gray-900">Frequently Asked Questions</h2>
              </div>
              <div className="divide-y divide-gray-100">
                {faqs.map((faq, index) => (
                  <div key={index} className="px-6 py-4">
                    <button
                      onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                      className="w-full flex items-center justify-between text-left"
                    >
                      <span className="font-semibold text-gray-900">{faq.question}</span>
                      {expandedFaq === index ? (
                        <ChevronUpIcon className="h-5 w-5 text-gray-500" />
                      ) : (
                        <ChevronDownIcon className="h-5 w-5 text-gray-500" />
                      )}
                    </button>
                    {expandedFaq === index && (
                      <p className="mt-3 text-sm text-gray-600">{faq.answer}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white rounded-volexx shadow-volexx border border-gray-100">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-xl font-bold text-gray-900">Submit a Support Ticket</h2>
              </div>
              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Subject
                  </label>
                  <input
                    type="text"
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-volexx focus:ring-2 focus:ring-volexx-blue-500 focus:border-transparent"
                    placeholder="Briefly describe your issue"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Message
                  </label>
                  <textarea
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    rows={6}
                    className="w-full px-3 py-2 border border-gray-300 rounded-volexx focus:ring-2 focus:ring-volexx-blue-500 focus:border-transparent"
                    placeholder="Provide details about your issue..."
                    required
                  />
                </div>
                <button
                  type="submit"
                  className="w-full bg-volexx-orange-600 hover:bg-volexx-orange-700 text-white font-semibold py-3 rounded-volexx transition-colors"
                >
                  Submit Ticket
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RiderSupport;
